import { Col, Row } from 'antd';
import { ComponentType, Fragment } from 'react';
import { Field as FieldRFF, FieldRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import Input from 'components/Form/Input';
import SectionTitle from 'components/SectionTitle';
import SubTitle from 'components/SubTitle';

export interface FormFieldsConfig {
  id: string;
  label?: string | null;
  tip?: boolean;
  required?: boolean;
  fullWith?: boolean;
  isSectionTitle?: boolean;
  nested?: FormFieldsConfig[];
  component?: ComponentType<FieldRenderProps<string, HTMLElement, string>>;
  options?: {
    value: any;
    label: string;
  }[];
  min?: number;
  max?: number;
  precision?: number;
  disabled?: boolean;
}

export interface DocumentFormProps {
  formFields: FormFieldsConfig[];
  submitError?: Record<string, string> | null;
  clearSubmitError?: (data: Record<string, any>) => void;
}

const DocumentForm = ({ formFields, submitError, clearSubmitError }: DocumentFormProps) => {
  const { t } = useTranslation();

  return (
    <>
      <SectionTitle>{t('certificate.generalInformation')}</SectionTitle>
      <Row gutter={60} align="bottom" style={{ alignItems: 'flex-start' }}>
        {formFields.map((field, index) => {
          if (field.isSectionTitle) {
            return (
              <Col md={24} key={field.id}>
                <SectionTitle>{t(`certificate.general.${field.id}.label`)}</SectionTitle>
              </Col>
            );
          }

          if (!field.nested) {
            return (
              <Col md={24} lg={field.fullWith ? 24 : 12} key={field.id}>
                <FieldRFF
                  name={field.id}
                  label={t(`certificate.general.${field.label ?? field.id}.label`)}
                  tip={field.tip && t(`certificate.general.${field.id}.tip`)}
                  placeholder={t(`certificate.general.${field.label ?? field.id}.placeholder`)}
                  data-cy={`form_${field.id}`}
                  required={field.required}
                  component={field.component || Input}
                  options={field.options}
                  submitError={submitError}
                  clearSubmitError={clearSubmitError}
                  disabled={field.disabled}
                />
              </Col>
            );
          }

          return (
            <Fragment key={`${field.id}-${index}`}>
              {field.label !== null && (
                <Col md={24}>
                  <SubTitle>{t(`certificate.general.${field.label ?? field.id}.label`)}</SubTitle>
                </Col>
              )}

              {field.nested.map(({ fullWith, ...nestedField }) => (
                <Col key={`${field.id}-${nestedField.id}`} md={24} lg={fullWith ? 24 : 12}>
                  <FieldRFF
                    {...nestedField}
                    name={`${field.id}.${nestedField.id}`}
                    label={t(`certificate.general.${nestedField.label ?? nestedField.id}.label`)}
                    placeholder={t(
                      `certificate.general.${nestedField.label ?? nestedField.id}.placeholder`
                    )}
                    data-cy={`form_${nestedField.id}`}
                    required={nestedField.required}
                    component={nestedField.component || Input}
                    submitError={submitError}
                    clearSubmitError={clearSubmitError}
                    disabled={field.disabled}
                  />
                </Col>
              ))}
            </Fragment>
          );
        })}
      </Row>
    </>
  );
};

export default DocumentForm;
